// Single point that writes to the `clients` table for the Item 11 import
// flows. Components never write directly. Consumed by every path.

import { useCallback } from 'react';
import { supabase } from '../../../../lib/supabase';
import { useAuthStore } from '../../../../stores/authStore';
import type { ImportClientsPath } from '../types';
import { classifyMatch } from './dedupe';
import { normalizeEmail, normalizeName, normalizePhone } from './normalize';
import type {
  BatchInsertResult,
  DuplicateMatch,
  ExistingClient,
  ImportClientInput,
  NormalizedRow,
} from './types';

const BATCH_SIZE = 50;

// The ImportClientsPath union uses short identifiers (`spreadsheet`, …) while
// the `clients.source` check constraint (migration 126) requires the prefixed
// form (`import_spreadsheet`, …). This is the single point of translation —
// it must match the SQL constraint exactly. Keep in lock-step with migration
// 126 and the ImportClientsPath union.
function pathToDbSource(path: ImportClientsPath): string {
  return `import_${path}`;
}

export interface UseImportClients {
  insertClient: (
    row: ImportClientInput,
  ) => Promise<{ id: string } | { error: string }>;

  insertClientsBatch: (
    rows: ImportClientInput[],
    source: ImportClientsPath,
    onProgress: (done: number, total: number) => void,
  ) => Promise<BatchInsertResult>;

  findDuplicates: (rows: NormalizedRow[]) => Promise<DuplicateMatch[]>;

  // Targeted single-phone duplicate check for the conversational blur
  // handler. Returns the first client in the business whose normalized
  // phone equals the incoming phone, or null. Returns null on error —
  // blur is a UX hint, not a critical path.
  findPhoneMatch: (
    phone: string,
  ) => Promise<{ existingClientId: string; existingClientName: string } | null>;

  // Fill missing phone/email on an existing client from an incoming row.
  // NEVER overwrites a non-empty existing field. If nothing is eligible,
  // returns { merged: false, updatedFields: [] }.
  mergeIntoExisting: (
    existingClientId: string,
    incoming: ImportClientInput,
  ) => Promise<
    | { merged: boolean; updatedFields: string[] }
    | { error: string }
  >;

  // Remove a client from the current business. Used by Path 1 when the user
  // taps the × on a row that has already been saved. Scoped by id AND
  // business_id so a stale id cannot delete outside the current business.
  deleteClient: (
    clientId: string,
  ) => Promise<{ deleted: true } | { error: string }>;
}

// Columns the import flow will consider fillable on merge. Restricted to the
// fields `ImportClientInput` carries. `name` is intentionally excluded — it
// is a required column so existing rows always have it, which means the
// "only fill empty" rule would never let us touch it anyway.
const MERGE_CANDIDATE_COLUMNS = ['phone', 'email'] as const;
type MergeColumn = (typeof MERGE_CANDIDATE_COLUMNS)[number];

function isEmpty(v: string | null | undefined): boolean {
  return v == null || String(v).trim() === '';
}

export function useImportClients(): UseImportClients {
  const businessId = useAuthStore(s => s.business?.id ?? null);

  const insertClient = useCallback(
    async (row: ImportClientInput) => {
      if (!businessId) return { error: 'No active business' };
      const name = row.name.trim();
      if (!name) return { error: 'Name is required' };

      const { data, error } = await supabase
        .from('clients')
        .insert({
          business_id: businessId,
          name,
          phone: row.phone?.trim() || null,
          email: row.email?.trim() || null,
          source: pathToDbSource(row.source),
        })
        .select('id')
        .maybeSingle();

      if (error) return { error: error.message };
      if (!data) return { error: 'Insert returned no row' };
      return { id: data.id };
    },
    [businessId],
  );

  const insertClientsBatch = useCallback(
    async (
      rows: ImportClientInput[],
      source: ImportClientsPath,
      onProgress: (done: number, total: number) => void,
    ): Promise<BatchInsertResult> => {
      const result: BatchInsertResult = { succeeded: 0, failed: [] };
      if (!businessId) {
        return {
          succeeded: 0,
          failed: rows.map(r => ({ row: r, error: 'No active business' })),
        };
      }

      const total = rows.length;
      onProgress(0, total);

      for (let i = 0; i < rows.length; i += BATCH_SIZE) {
        const slice = rows.slice(i, i + BATCH_SIZE);
        const dbSource = pathToDbSource(source);
        const payload = slice.map(r => ({
          business_id: businessId,
          name: r.name.trim(),
          phone: r.phone?.trim() || null,
          email: r.email?.trim() || null,
          source: dbSource,
        }));

        const { data, error } = await supabase
          .from('clients')
          .insert(payload)
          .select('id');

        if (error) {
          // The whole batch failed — attribute the error to every row in the
          // batch. A finer-grained retry could fall back to per-row inserts,
          // but Phase 2b ships batch-level error attribution.
          for (const r of slice) {
            result.failed.push({ row: r, error: error.message });
          }
        } else {
          result.succeeded += data?.length ?? 0;
        }

        onProgress(Math.min(i + slice.length, total), total);
      }

      return result;
    },
    [businessId],
  );

  const findDuplicates = useCallback(
    async (rows: NormalizedRow[]): Promise<DuplicateMatch[]> => {
      if (!businessId || rows.length === 0) return [];

      const { data, error } = await supabase
        .from('clients')
        .select('id, name, phone, email')
        .eq('business_id', businessId);

      if (error || !data) return [];

      const existing: ExistingClient[] = data.map(c => ({
        id: c.id as string,
        name: normalizeName(c.name as string | null),
        phone: normalizePhone(c.phone as string | null),
        email: normalizeEmail(c.email as string | null),
      }));

      const matches: DuplicateMatch[] = [];
      for (const row of rows) {
        const m = classifyMatch(row, existing);
        if (m) matches.push(m);
      }
      return matches;
    },
    [businessId],
  );

  const mergeIntoExisting = useCallback(
    async (
      existingClientId: string,
      incoming: ImportClientInput,
    ): Promise<
      | { merged: boolean; updatedFields: string[] }
      | { error: string }
    > => {
      if (!businessId) return { error: 'No active business' };

      const { data: existing, error: readError } = await supabase
        .from('clients')
        .select('id, name, phone, email')
        .eq('id', existingClientId)
        .eq('business_id', businessId)
        .maybeSingle();

      if (readError) return { error: readError.message };
      if (!existing) return { error: 'Existing client not found' };

      const incomingByColumn: Record<MergeColumn, string | undefined> = {
        phone: incoming.phone?.trim() || undefined,
        email: incoming.email?.trim() || undefined,
      };

      const staged: Record<string, string> = {};
      const updatedFields: string[] = [];

      for (const col of MERGE_CANDIDATE_COLUMNS) {
        const existingVal = (existing as Record<string, unknown>)[col] as
          | string
          | null
          | undefined;
        const incomingVal = incomingByColumn[col];
        if (isEmpty(existingVal) && !isEmpty(incomingVal)) {
          staged[col] = incomingVal as string;
          updatedFields.push(col);
        }
      }

      if (updatedFields.length === 0) {
        return { merged: false, updatedFields: [] };
      }

      const { error: updateError } = await supabase
        .from('clients')
        .update(staged)
        .eq('id', existingClientId)
        .eq('business_id', businessId);

      if (updateError) return { error: updateError.message };

      return { merged: true, updatedFields };
    },
    [businessId],
  );

  const findPhoneMatch = useCallback(
    async (
      phone: string,
    ): Promise<{ existingClientId: string; existingClientName: string } | null> => {
      const normalizedPhone = normalizePhone(phone);
      if (!normalizedPhone || !businessId) return null;

      const { data, error } = await supabase
        .from('clients')
        .select('id, name')
        .eq('business_id', businessId)
        .eq('phone', normalizedPhone)
        .limit(1)
        .maybeSingle();

      if (error || !data) return null;
      return {
        existingClientId: data.id as string,
        existingClientName: (data.name as string) ?? '',
      };
    },
    [businessId],
  );

  const deleteClient = useCallback(
    async (
      clientId: string,
    ): Promise<{ deleted: true } | { error: string }> => {
      if (!businessId) return { error: 'No active business' };

      const { error } = await supabase
        .from('clients')
        .delete()
        .eq('id', clientId)
        .eq('business_id', businessId);

      if (error) return { error: error.message };
      return { deleted: true };
    },
    [businessId],
  );

  return {
    insertClient,
    insertClientsBatch,
    findDuplicates,
    findPhoneMatch,
    mergeIntoExisting,
    deleteClient,
  };
}
